import React, { useState } from "react";
import BlockMapper from "modules/forms/hosted/blocks/BlockMapper";
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import useTrans from "components/hooks/useTrans";
import useHttp from "components/hooks/useHttp";
import useToast from "components/hooks/useToast";
import useFormFlow from "modules/forms/hooks/useFormFlow";
import { asFormData, scrollToTop } from "core/helpers";
import useForm from "modules/forms/hooks/useForm";
import useRouting from "hooks/useRouting";
import ReCaptcha from "modules/forms/components/recaptcha/ReCaptcha";
import useReCaptcha from "modules/forms/components/recaptcha/useReCaptcha";

export default function CustomPage() {
    const { t } = useTrans();
    const { form } = useForm();
    const { push } = useRouting();
    const recaptchaRef = useReCaptcha();
    const { values, setIsSubmitting } = useFormFlow();
    const { errorToast, successToast } = useToast();
    const apiUrl = `/forms/${form.uuid}/private/submit`;
    const http = useHttp();
    const customPages = form.pages.filter(p => p.type == "custom");
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const currentPage = customPages[currentPageIndex];
    const hasPrevious = currentPageIndex > 0;
    const nextPage = customPages[currentPageIndex + 1];

    const submit = async () => {
        setIsSubmitting(true);

        // If the next page is custom success, it's time to submit
        if (!nextPage) {
            const recaptchaToken = await recaptchaRef.current.executeAsync();
            const formData = asFormData(values);

            http
                .withCaptcha(recaptchaToken)
                .post(apiUrl, formData)
                .then(() => {
                    successToast();
                    scrollToTop();
                    push(`/forms/${form.uuid}/private/authenticated/custom-success`);
                })
                .catch(e => {
                    errorToast(e);
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        } else {
            setIsSubmitting(false);
            setCurrentPageIndex(prevState => prevState + 1);

            scrollToTop();
        }
    };

    return (
        <form
            onSubmit={e => {
                e.preventDefault();
                submit();
            }}
        >
            {hasPrevious && (
                <button
                    onClick={() => setCurrentPageIndex(prevState => prevState - 1)}
                    type="button"
                    className="mb-8 me-2 inline-flex items-center rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-center text-sm font-medium text-gray-100 transition-all hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-100"
                >
                    <ArrowLeftIcon className={"size-4 fill-black text-black"}/>
                    <span className={"ml-3"}>{t("back")}</span>
                </button>
            )}

            {currentPage.blocks.map(block => (
                <BlockMapper
                    key={block.uuid}
                    block={block}
                />
            ))}

            <ReCaptcha captchaRef={recaptchaRef} hideBadge={false}/>
        </form>
    );
}
